import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { MainLayout, ErrorPageLayout, ConfirmButton } from 'src/components';
import { PAGES } from 'src/constants/pages';
import styles from './Error404Page.module.scss';
import { ACCOUNT_ROLES } from 'src/constants/globalConstants';
import { getCompanyWorkspacesHook } from 'src/hooks/getCompanyWorkspacesHook';
import { useSelector } from 'react-redux';
import { userRoleSelector } from 'src/redux/selectors';
import { getCurrentWorkspaceFromLocalStorage } from 'src/utils/comon';
import { useUserValues } from 'src/hooks';

const errorReasons = [
  { text: '- We are doing some maintenance and the page will be back soon', newRow: '' },
  { text: '- The page no longer exists', newRow: '' },
  { text: '- One of of engineers tripped over a cable for the server', newRow: '(not again, James)' },
  { text: '- You like 404 Pages', newRow: '' }
];

const BTN_PADDING = 42;

const Error404Page = (): JSX.Element => {
  const { push } = useHistory();
  const userRole = useSelector(userRoleSelector);
  const { email } = useUserValues();
  const { companyWorkspaces } = getCompanyWorkspacesHook();

  const selectedWorkspace = useMemo(() => getCurrentWorkspaceFromLocalStorage(email), [email]);

  const onClick = useCallback(() => {
    const hasCompanyWorkspaces = !!companyWorkspaces?.length;

    const redirectPages = {
      [ACCOUNT_ROLES.USER]: selectedWorkspace ? PAGES.DASHBOARD : PAGES.WORKSPACES_CHOOSE,
      [ACCOUNT_ROLES.COMPANY]: hasCompanyWorkspaces ? PAGES.WORKSPACES_MANAGEMENT : PAGES.WORKSPACES_CREATE,
      [ACCOUNT_ROLES.MULTIUSER]: PAGES.CHOOSE_ACCOUNT_TYPE
    };

    push(redirectPages[userRole] || PAGES.HOME);
  }, [companyWorkspaces?.length, push, selectedWorkspace, userRole]);

  return (
    <MainLayout isCentered={false} isPrivate={false}>
      <ErrorPageLayout>
        <div className={styles.wrapper}>
          <h2 className={styles.title}>
            <span>404. </span>
            Page Not Found.
          </h2>
          <p className={styles.description}>
            Whoops! That’s an error.
            <span>The page you have requested isn’t available right now.</span>
            <span>Perhaps you are here because:</span>
          </p>

          <ul className={styles.list}>
            {errorReasons.map(({ text, newRow }, idx) => (
              <li key={idx} className={styles.listItem}>
                <p className={styles.text}>
                  {text}
                  {!!newRow && <span>{newRow}</span>}
                </p>
              </li>
            ))}
          </ul>

          <ConfirmButton type="button" onClick={onClick} horizontalPadding={BTN_PADDING}>
            Return to Platform
          </ConfirmButton>
        </div>
      </ErrorPageLayout>
    </MainLayout>
  );
};

export default Error404Page;
